import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, timeAgo } from "@/lib/api";
import { CONSTRAINTS } from "@/data/forge";
import { ICONS } from "@/components/site/Icons";

export default function CommunityBlock() {
  const [winner, setWinner] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/forge-of-the-week").then(({ data }) => setWinner(data)).catch(() => setWinner(null));
    api.get("/posts", { params: { limit: 3 } }).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  return (
    <section
      data-testid="community-teaser-section"
      className="py-12 md:py-16"
      style={{ background: "linear-gradient(180deg,transparent,rgba(255,52,52,.035),transparent)" }}
    >
      <div className="mx-auto w-[min(1180px,calc(100%-32px))]">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65 }}
            className="rounded-[26px] border border-[#332020] p-8 md:p-10"
            style={{ background: "radial-gradient(circle at 20% 0%, rgba(255,52,52,.1), transparent 60%), #0b0b0c" }}
          >
            <div className="eyebrow-label mb-4">Join the Forge</div>
            <h2 className="headline text-3xl sm:text-4xl lg:text-5xl mb-4">
              <span className="text-white">What would </span>
              <span className="text-forge">you forge?</span>
            </h2>
            <p className="text-sm text-white/50 leading-relaxed mb-6">
              You've got 20 minutes. Share your Pouches, discover new creations, challenge your
              friends and reforge the ideas you find.
            </p>
            <div className="flex flex-wrap gap-2 mb-7">
              {CONSTRAINTS.map((c, i) => {
                const Icon = ICONS[c.icon];
                return (
                  <span
                    key={c.label}
                    data-testid={`constraint-${i}`}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[12px] text-white/70 hover:border-ember/60 transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5 text-ember" />
                    {c.label}
                  </span>
                );
              })}
            </div>
            <Link
              to="/community"
              data-testid="teaser-cta"
              className="display inline-block text-[11px] tracking-[0.22em] px-7 py-3.5 rounded-full bg-forge text-white hover:bg-ember transition-colors"
            >
              Post what you'd forge
            </Link>

            {posts.length > 0 && (
              <div className="mt-8 space-y-2" data-testid="teaser-feed">
                {posts.map((post) => (
                  <Link
                    key={post.post_id}
                    to={`/community/${post.post_id}`}
                    data-testid={`teaser-post-${post.post_id}`}
                    className="flex items-center justify-between gap-4 border-t border-white/8 pt-2.5 text-[12px] text-white/50 hover:text-ember transition-colors"
                  >
                    <span className="truncate">{post.title}</span>
                    <span className="shrink-0 text-white/25">{timeAgo(post.created_at)}</span>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            data-testid="forge-of-the-week-section"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="relative rounded-[26px] border border-forge/25 overflow-hidden p-8 md:p-10"
            style={{ background: "radial-gradient(circle at 80% 0%, rgba(238,101,51,.12), transparent 62%), #0a0a0b" }}
          >
            <span className="absolute top-0 left-0 right-0 h-[3px] ember-line" />
            <div className="eyebrow-label mb-4">Forge of the week</div>
            {winner ? (
              <div data-testid="fotw-winner">
                <h3 className="headline text-2xl md:text-4xl mb-3">{winner.title}</h3>
                <p className="text-[12px] text-white/35 mb-5">
                  {winner.creation_type === "pouch" ? "Pouch" : "Dice"} · ♥ {winner.like_count} this week ·
                  forged by {winner.author_name}
                </p>
                {winner.contents?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {winner.contents.slice(0, 6).map((c, i) => (
                      <span key={i} className="text-[11px] px-2.5 py-1 rounded-lg bg-white/[0.05] text-white/70">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                <Link
                  to={`/community/${winner.post_id}`}
                  data-testid="fotw-link"
                  className="display inline-block text-[11px] tracking-[0.22em] px-7 py-3.5 rounded-full border border-forge text-forge hover:bg-forge hover:text-white transition-colors"
                >
                  See the build
                </Link>
              </div>
            ) : (
              <div data-testid="fotw-empty">
                <h3 className="headline text-2xl md:text-4xl mb-3">The spotlight is empty</h3>
                <p className="text-sm text-white/45 mb-6">
                  The most-liked creation of the last seven days lands here automatically. Post yours
                  and you take the spot.
                </p>
                <Link
                  to="/community/new?kind=creation"
                  data-testid="fotw-empty-cta"
                  className="display inline-block text-[11px] tracking-[0.22em] px-7 py-3.5 rounded-full border border-forge text-forge hover:bg-forge hover:text-white transition-colors"
                >
                  Post a creation
                </Link>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
